import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getMasterPlan, getSkuHistory } from '../lib/api';
import { ChartLineUp, Warning, CheckCircle, Calendar, ArrowsClockwise, TrendDown, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';

const riskColors = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  low: 'bg-amber-100 text-amber-800 border-amber-200',
  moderate: 'bg-blue-100 text-blue-800 border-blue-200',
  healthy: 'bg-green-100 text-green-800 border-green-200',
  no_data: 'bg-muted text-muted-foreground border-border',
};

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className={`${bg} border p-4`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest opacity-70">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
      </div>
      <Icon size={24} weight="bold" className={`${color} opacity-60`} />
    </div>
  </div>
);

export default function Forecast() {
  const [data, setData] = useState({ summary: {}, plan: [] });
  const [loading, setLoading] = useState(true);
  const [lookback, setLookback] = useState(30);
  const [leadTime, setLeadTime] = useState(7);
  const [riskFilter, setRiskFilter] = useState('');
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { lookback_days: lookback, lead_time_days: leadTime };
      if (riskFilter) params.risk_filter = riskFilter;
      const res = await getMasterPlan(params);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [lookback, leadTime, riskFilter]);

  const openHistory = async (sku) => {
    setSelectedSku(sku);
    setHistoryLoading(true);
    try {
      const res = await getSkuHistory(sku.sku_id, { lookback_days: 60, forecast_days: 14 });
      setHistoryData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const combinedChartData = historyData ? [
    ...historyData.history.map(h => ({ date: h.date, actual: h.consumption, predicted: null })),
    ...historyData.predictions.map(p => ({ date: p.date, actual: null, predicted: p.consumption }))
  ] : [];

  return (
    <Layout>
      <div data-testid="forecast-page" className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Stock Forecast & Master Plan</h1>
          <p className="text-muted-foreground">Days-of-cover analysis with reorder suggestions</p>
        </div>

        {/* Controls */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="overline">Lookback (days)</Label>
            <Input type="number" min={1} max={365} value={lookback} onChange={(e) => setLookback(parseInt(e.target.value))} className="mt-1" data-testid="lookback-input" />
          </div>
          <div>
            <Label className="overline">Lead Time (days)</Label>
            <Input type="number" min={0} max={180} value={leadTime} onChange={(e) => setLeadTime(parseInt(e.target.value))} className="mt-1" data-testid="leadtime-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Risk Filter</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              <Button size="sm" variant={riskFilter === '' ? 'default' : 'outline'} onClick={() => setRiskFilter('')} className={riskFilter === '' ? 'bg-brand' : ''}>All</Button>
              {['critical', 'low', 'moderate', 'healthy', 'no_data'].map(r => (
                <Button key={r} size="sm" variant={riskFilter === r ? 'default' : 'outline'} onClick={() => setRiskFilter(r)} className={riskFilter === r ? 'bg-brand' : ''}>
                  {r.replace('_', ' ')}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total SKUs" value={data.summary?.total_skus || 0} icon={ChartLineUp} color="text-foreground" bg="bg-card border-border rounded-lg" />
          <StatCard label="Critical" value={data.summary?.critical || 0} icon={Warning} color="text-red-700" bg="bg-red-50 border-red-200" />
          <StatCard label="Low Stock" value={data.summary?.low_stock || 0} icon={TrendDown} color="text-amber-700" bg="bg-amber-50 border-amber-200" />
          <StatCard label="Healthy" value={data.summary?.healthy || 0} icon={CheckCircle} color="text-green-700" bg="bg-green-50 border-green-200" />
          <StatCard label="Reorder Needed" value={data.summary?.reorder_suggested_count || 0} icon={ArrowsClockwise} color="text-white" bg="bg-brand border-brand" />
        </div>

        {/* Master Plan Table */}
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Risk</th>
                <th>Current Stock</th>
                <th>Avg Daily Use</th>
                <th>Days of Cover</th>
                <th>Stockout Date</th>
                <th>Reorder Qty</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
              ) : !data.plan || data.plan.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12">
                  <ChartLineUp size={40} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-muted-foreground">No SKUs match filter</p>
                </td></tr>
              ) : data.plan.map(p => (
                <tr
                  key={p.sku_id}
                  className={`cursor-pointer hover:bg-muted ${p.risk === 'critical' ? 'bg-red-50' : ''}`}
                  onClick={() => openHistory(p)}
                  data-testid={`forecast-row-${p.sku_code}`}
                >
                  <td className="font-mono font-semibold text-brand underline">{p.sku_code}</td>
                  <td>{p.sku_name}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-bold uppercase border ${riskColors[p.risk] || 'bg-muted'}`}>
                      {p.risk?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="font-mono font-bold">{p.current_stock}</td>
                  <td className="font-mono">{p.avg_daily_usage}</td>
                  <td className="font-mono">
                    {p.days_of_cover !== null ? `${p.days_of_cover}d` : '—'}
                  </td>
                  <td className="text-sm text-muted-foreground">
                    {p.projected_stockout ? new Date(p.projected_stockout).toLocaleDateString() : '—'}
                  </td>
                  <td className="font-mono">
                    {p.reorder_suggested ? (
                      <span className="font-bold text-brand">+{p.suggested_reorder_qty}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* History + Prediction — slide-in right Sheet */}
        <Sheet open={!!selectedSku} onOpenChange={() => { setSelectedSku(null); setHistoryData(null); }}>
          <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="history-dialog">
            <SheetHeader>
              <SheetTitle>
                {selectedSku?.sku_name} · Historical Consumption + ML Prediction
              </SheetTitle>
            </SheetHeader>
            {historyLoading ? (
              <div className="py-12 text-center text-muted-foreground">Loading chart...</div>
            ) : historyData ? (
              <div className="mt-4">
                <div className="flex items-center gap-4 mb-4 text-sm flex-wrap">
                  <div className="px-3 py-1 bg-muted rounded-md">
                    <span className="overline mr-2">Method</span>
                    <span className="font-mono font-semibold">{historyData.method}</span>
                  </div>
                  <div className="px-3 py-1 bg-muted rounded-md">
                    <span className="overline mr-2">Alpha (smoothing)</span>
                    <span className="font-mono font-semibold">{historyData.alpha}</span>
                  </div>
                  <div className="px-3 py-1 bg-muted rounded-md">
                    <span className="overline mr-2">History</span>
                    <span className="font-mono font-semibold">{historyData.history?.length}d</span>
                  </div>
                  <div className="px-3 py-1 bg-brand text-brand-foreground rounded-md">
                    <span className="text-[11px] font-semibold uppercase tracking-wider mr-2 opacity-80">Predicted</span>
                    <span className="font-mono font-semibold">{historyData.predictions?.length}d ahead</span>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={combinedChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: 10 }}
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        color: 'hsl(var(--popover-foreground))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 6,
                      }}
                    />
                    <Legend />
                    <ReferenceLine
                      x={historyData.history[historyData.history.length - 1]?.date}
                      stroke="hsl(var(--destructive))"
                      strokeDasharray="3 3"
                      label={{ value: 'Now', fill: 'hsl(var(--destructive))', fontSize: 10 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="hsl(var(--brand))"
                      strokeWidth={2}
                      dot={false}
                      name="Actual Consumption"
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="predicted"
                      stroke="hsl(var(--success))"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="ML Prediction"
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
